import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { getCategory } from '../../graphql/queries';
import { updateCategory, deleteCategory } from '../../graphql/mutation';
import { 
  Card, 
  Form, 
  Input, 
  InputNumber,
  Button, 
  Space, 
  Row, 
  Col,
  Descriptions,
  Modal
} from 'antd';
import { SaveOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { showSnackBar } from '../../components/SnackBar/SnackBar';
import './CategoryDetail.css';

const CategoryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const { data: categoryData, loading: fetchLoading } = useQuery(getCategory, {
    variables: { categoryId: id },
  });

  const [updateCategoryMutation] = useMutation(updateCategory);
  const [deleteCategoryMutation] = useMutation(deleteCategory);

  useEffect(() => {
    if (categoryData?.getCategory) {
      const category = categoryData.getCategory;
      form.setFieldsValue({
        name: category.name,
        rowNumber: category.rowNumber,
      });
    }
  }, [categoryData, form]);

  const handleUpdate = async (values) => {
    try {
      setLoading(true);

      await updateCategoryMutation({
        variables: {
          categoryId: id,
          name: values.name,
          rowNumber: values.rowNumber,
        }
      });

      showSnackBar('success', 'Success', 'Category updated successfully');
      setIsEditing(false);
      // Refetch the data to show updated values
      window.location.reload();
    } catch (error) {
      showSnackBar('error', 'Error', error.message || 'Failed to update category');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    Modal.confirm({
      title: 'Delete Category',
      content: 'Are you sure you want to delete this category? This action cannot be undone.',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await deleteCategoryMutation({
            variables: { categoryId: id }
          });
          showSnackBar('success', 'Success', 'Category deleted successfully');
          navigate('/categories');
        } catch (error) {
          showSnackBar('error', 'Error', 'Failed to delete category');
        }
      }
    });
  };

  const category = categoryData?.getCategory;

  if (fetchLoading) {
    return (
      <div className="category-detail-container">
        <Card loading />
      </div>
    );
  }

  if (!category) {
    return (
      <div className="category-detail-container">
        <Card title="Category Not Found">
          <p>The requested category could not be found.</p>
          <Button onClick={() => navigate('/categories')}>
            Back to Categories
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="category-detail-container">
      <Card 
        title={`Category: ${category.name}`}
        extra={
          <Space>
            {!isEditing && (
              <Button 
                type="primary" 
                icon={<EditOutlined />}
                onClick={() => setIsEditing(true)}
              >
                Edit
              </Button>
            )}
            <Button 
              danger 
              icon={<DeleteOutlined />}
              onClick={handleDelete}
            >
              Delete
            </Button>
          </Space>
        }
      >
        {!isEditing ? (
          <div className="category-details-view">
            <Descriptions column={1} bordered>
              <Descriptions.Item label="ID">{category.id}</Descriptions.Item>
              <Descriptions.Item label="Name">{category.name}</Descriptions.Item>
              <Descriptions.Item label="Row Number">{category.rowNumber}</Descriptions.Item>
              <Descriptions.Item label="Created At">
                {new Date(category.createdAt).toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="Updated At">
                {new Date(category.updatedAt).toLocaleString()}
              </Descriptions.Item>
              {category.tools && category.tools.length > 0 && (
                <Descriptions.Item label="Associated Tools">
                  {category.tools.map(tool => tool.name).join(', ')}
                </Descriptions.Item>
              )}
            </Descriptions>
          </div>
        ) : (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleUpdate}
          >
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  name="name"
                  label="Name"
                  rules={[{ required: true, message: 'Please enter category name' }]}
                >
                  <Input placeholder="Enter category name" />
                </Form.Item>

                <Form.Item
                  name="rowNumber"
                  label="Row Number"
                  rules={[{ required: true, message: 'Please enter row number' }]}
                >
                  <InputNumber 
                    min={1} 
                    placeholder="Enter row number"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item>
              <Space>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={loading}
                  icon={<SaveOutlined />}
                >
                  Update Category
                </Button>
                <Button onClick={() => setIsEditing(false)}>
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        )}
      </Card>
    </div>
  );
};

export default CategoryDetail; 